import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

const legalTypes = [
  { key: 'impressum', label: 'Impressum' },
  { key: 'datenschutz', label: 'Datenschutz' },
  { key: 'affiliate_hinweis', label: 'Affiliate Hinweis' },
  { key: 'partner_hinweis', label: 'Partner Hinweis' },
];

export default function LegalEditor() {
  const [texts, setTexts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');

  useEffect(() => {
    const loadAll = async () => {
      try {
        const results = await Promise.all(
          legalTypes.map(t => adminAPI.getLegal(t.key))
        );
        const obj = {};
        results.forEach((res, i) => {
          obj[legalTypes[i].key] = res.data.content || '';
        });
        setTexts(obj);
      } catch {} finally { setLoading(false); }
    };
    loadAll();
  }, []);

  const handleSave = async (type) => {
    setSaving(type);
    try {
      await adminAPI.updateLegal(type, texts[type]);
      toast.success(`${type} gespeichert`);
    } catch {
      toast.error('Fehler beim Speichern');
    } finally { setSaving(''); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0E518D]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="legal-editor">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Rechtstexte</h1>
        <p className="text-sm text-slate-500 font-body mt-1">Impressum, Datenschutz und Hinweise bearbeiten</p>
      </div>

      <Tabs defaultValue="impressum">
        <TabsList className="bg-white border border-slate-200 rounded-sm">
          {legalTypes.map(t => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              className="font-body text-sm data-[state=active]:bg-[#0E518D] data-[state=active]:text-white rounded-sm"
              data-testid={`legal-tab-${t.key}`}
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {legalTypes.map(t => (
          <TabsContent key={t.key} value={t.key}>
            <Card className="border-0 shadow-sm rounded-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-body font-semibold text-slate-700">{t.label}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  value={texts[t.key] || ''}
                  onChange={(e) => setTexts(prev => ({ ...prev, [t.key]: e.target.value }))}
                  className="rounded-sm font-body min-h-[300px]"
                  data-testid={`legal-textarea-${t.key}`}
                />
                <Button
                  onClick={() => handleSave(t.key)}
                  disabled={saving === t.key}
                  className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm font-body"
                  data-testid={`legal-save-${t.key}`}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving === t.key ? 'Speichern...' : 'Speichern'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
